import React from 'react';
import { Placement } from '@popperjs/core';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import { MeetingLocation } from 'flow/utils/meeting-constants';
import AnchoredPopover from './AnchoredPopover';
import CenteredDialog from './CenteredDialog';
import MeetingDeviceSettings from './MeetingDeviceSettings';
import MeetingShowIncomingVideoToggle from './MeetingShowIncomingVideoToggle';

interface Props {
    onClose: () => void;
    /**
     * When provided, renders as a popover anchored to this element (the footer
     * button). Without it, renders as a centered modal dialog — matching the
     * effects picker, which is how the more-menu opens it once the footer
     * collapses.
     */
    anchorEl?: HTMLElement | null;
    placement?: Placement;
}

/** The always-dark device-settings panel, as an anchored popover or a modal. */
const MeetingDeviceSettingsDialog = ({
    onClose,
    anchorEl,
    placement = 'top-start',
}: Props) => {
    const { i18next } = useAppContext();

    const panelClassName =
        'no-dark shadow-base flex flex-col gap-3 rounded-xl bg-gray-800 pb-6 text-white';

    const panelBody = (
        <>
            {/* Header */}
            <div className="flex h-15 items-center justify-between ps-6 pe-3">
                <h2 className="text-2xl font-semibold">
                    {i18next.t('settings')}
                </h2>
                <button
                    type="button"
                    onClick={onClose}
                    className="focus-ring flex size-9 items-center justify-center rounded-full border-none bg-transparent text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
                    aria-label={i18next.t('close')}
                >
                    <Icon iconKey="close" size="1rem" />
                </button>
            </div>

            {/* TODO #video change this after video is fixed. */}
            <div className="px-6">
                <MeetingDeviceSettings
                    meetingLocation={MeetingLocation.ACTIVE_MEETING}
                    dark={true}
                />
                <MeetingShowIncomingVideoToggle />
            </div>
        </>
    );

    if (anchorEl) {
        return (
            <AnchoredPopover
                anchorEl={anchorEl}
                onClose={onClose}
                placement={placement}
                role="dialog"
                className={twMerge('w-99', panelClassName)}
            >
                {panelBody}
            </AnchoredPopover>
        );
    }

    return (
        <CenteredDialog
            onClose={onClose}
            role="dialog"
            className={panelClassName}
        >
            {panelBody}
        </CenteredDialog>
    );
};

export default MeetingDeviceSettingsDialog;
